import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import Select from "react-select";
import Logo from "../assets/img/Logo.png";
import Sidebar from "../components/Sidebar";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBell, faGlobe, faSpinner, faCheckCircle, faTimesCircle } from "@fortawesome/free-solid-svg-icons";
import "../assets/css/header.css";
import {
  getProfiles,
  getCurrentUser,
  getSugerencias,
  getAssistantModels,
} from "../services/API";
import { useNavigate } from "react-router-dom";
import alertify from "alertifyjs";
import "alertifyjs/build/css/alertify.css";
import "alertifyjs/build/css/themes/default.css";
import socket from "../assets/js/socket";

function Header() {
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const navigate = useNavigate();
  const [selectedAccountLabel, setSelectedAccountLabel] =
    useState("JandSProducts");
  const [selectedAccountCountry, setSelectedAccountCountry] = useState("US");
  const [selectedAccountEntity, setSelectedAccountEntity] = useState("");
  const [llmModels, setLlmModels] = useState([]);
  const [selectedLlmModel, setSelectedLlmModel] = useState("OpenAI");
  const [user, setUser] = useState("Name not available");
  const [sugerencias, setSugerencias] = useState([]);
  // Real-time report task tracking (Persisted in localStorage)
  const [activeReportTask, setActiveReportTask] = useState(() => {
    try {
      const saved = localStorage.getItem("activeReportTask");
      return saved ? JSON.parse(saved) : null;
    } catch (e) { return null; }
  });
  const [reportNotifications, setReportNotifications] = useState(() => {
    try {
      const saved = localStorage.getItem("reportNotifications");
      return saved ? JSON.parse(saved) : [];
    } catch (e) { return []; }
  });

  // Persist state changes
  useEffect(() => {
    if (activeReportTask) localStorage.setItem("activeReportTask", JSON.stringify(activeReportTask));
    else localStorage.removeItem("activeReportTask");
  }, [activeReportTask]);

  useEffect(() => {
    localStorage.setItem("reportNotifications", JSON.stringify(reportNotifications));
  }, [reportNotifications]);

  const getEnvModelOptions = () => {
    const raw = process.env.REACT_APP_LLM_ALLOWED_MODELS || "";
    const list = raw
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean)
      .map((item) => {
        const [provider, model] = item.split(":");
        if (!provider || !model) return null;
        const normalizedProvider = provider.toLowerCase();
        const providerLabelMap = {
          openai: "OpenAI",
          deepseek: "DeepSeek",
          gemini: "Gemini",
        };
        const labelProvider = providerLabelMap[normalizedProvider];
        if (!labelProvider) return null;

        let label = `${labelProvider} - ${model}`;
        if (normalizedProvider === "openai") {
          label = "GPT-4o Mini";
        } else if (normalizedProvider === "gemini") {
          label = "Gemini 2.0 Flash";
        }

        return {
          value: `${normalizedProvider}:${model}`,
          label: label,
        };
      })
      .filter(Boolean);

    return list;
  };

  const formatOptionLabel = ({ label, value, country, entity }) => (
    <div style={{ lineHeight: "1.2" }}>
      <span style={{ display: "block", marginBottom: "5px" }}>
        {label}
        <span style={{
          background: '#007bff',
          color: '#fff',
          borderRadius: '6px',
          padding: '2px 8px',
          fontSize: '0.75em',
          marginLeft: '8px',
          display: 'inline-block',
        }}>
          {country || "US"}
        </span>
        {entity && (
          <span
            style={{
              background: "#198754",
              color: "#fff",
              borderRadius: "6px",
              padding: "2px 8px",
              fontSize: "0.75em",
              marginLeft: "6px",
              display: "inline-block",
            }}
          >
            {entity}
          </span>
        )}
      </span>
      <span style={{ fontSize: "0.8em", color: "#000", display: "block" }}>
        ID: {value}
      </span>
    </div>
  );

  useEffect(() => {
    const storedLabel = localStorage.getItem("selectedAccountLabel");
    const storedCountry = localStorage.getItem("selectedAccountCountry");
    const storedEntity = localStorage.getItem("selectedAccountEntity");
    const storedModelLabel = localStorage.getItem("selectedLlmModelLabel");
    if (storedLabel) setSelectedAccountLabel(storedLabel);
    else setSelectedAccountLabel("No account selected");
    if (storedCountry) setSelectedAccountCountry(storedCountry);
    if (storedEntity) setSelectedAccountEntity(storedEntity);
    if (storedModelLabel) setSelectedLlmModel(storedModelLabel);
  }, []);

  const dropdown1Ref = useRef(null);
  const dropdown2Ref = useRef(null);
  const dropdown3Ref = useRef(null);
  const dropdownRefs = useMemo(() => ({
    dropdown1: dropdown1Ref,
    dropdown2: dropdown2Ref,
    dropdown3: dropdown3Ref,
  }), [dropdown1Ref, dropdown2Ref, dropdown3Ref]);

  const toggleDropdown = useCallback((dropdownName) => {
    setActiveDropdown((prev) => (prev === dropdownName ? null : dropdownName));
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const [profilesResp, currentUser, fetchedSugerencias, assistantModelsData] = await Promise.all([
          getProfiles().catch((err) => {
            console.warn("Failed to load Amazon profiles:", err.message);
            return { data: [] };
          }),
          getCurrentUser(),
          getSugerencias(),
          getAssistantModels().catch((err) => {
            console.warn("Failed to load assistant models:", err.message);
            return { models: [], defaultModel: null };
          }),
        ]);

        if (currentUser.userRolesId === 1 || currentUser.userRolesId === 2) {
          const currentSelectedAccountLabel = localStorage.getItem(
            "selectedAccountLabel",
          );
          if (
            fetchedSugerencias &&
            Array.isArray(fetchedSugerencias.data) &&
            currentSelectedAccountLabel
          ) {
            const filteredSugerencias = fetchedSugerencias.data.filter(
              (sug) => sug.ads_account === currentSelectedAccountLabel,
            );
            setSugerencias(filteredSugerencias);
          }
        }

        setUser(currentUser.firstName + " " + currentUser.lastName);
        localStorage.setItem(
          "name",
          currentUser.firstName + " " + currentUser.lastName,
        );

        const profiles = Array.isArray(profilesResp.data) ? profilesResp.data : [];

        const modelsResponse = Array.isArray(assistantModelsData?.models)
          ? assistantModelsData
          : { models: [], defaultModel: null };

        const modelOptions = modelsResponse.models.map((m) => ({
          label: m.label,
          value: m.value,
        }));

        const finalModelOptions = modelOptions.length > 0 ? modelOptions : getEnvModelOptions();

        if (finalModelOptions.length > 0) {
          setLlmModels(finalModelOptions);
          const storedModel = localStorage.getItem("selectedLlmModel");
          const defaultModel = modelsResponse.defaultModel || process.env.REACT_APP_LLM_DEFAULT_MODEL;
          
          const selected = finalModelOptions.find((m) => m.value === storedModel)
            || finalModelOptions.find((m) => m.value === defaultModel)
            || finalModelOptions[0];

          localStorage.setItem("selectedLlmModel", selected.value);
          localStorage.setItem("selectedLlmModelLabel", selected.label);
          setSelectedLlmModel(selected.label);
        }

        if (profiles.length === 0) {
          console.warn(
            "No Amazon profiles available. Amazon authentication may be required."
          );
          setAccounts([]);
          return;
        }

        // Mapping profiles to options
        const finalResult = profiles.map((p) => {
          const accountName = p.accountInfo?.name || `Profile ${p.profileId}`;
          const country = p.countryCode || "US";
          const entity = p.accountInfo?.type || "";

          return {
            label: accountName,
            value: String(p.profileId),
            country,
            entity,
          };
        });

        if (
          currentUser.userRolesId === 2 ||
          currentUser.userRolesId === 3 ||
          currentUser.userRolesId === 4
        ) {
          const profiles = JSON.parse(currentUser.profilesId);
          const filteredAccounts =
            finalResult.filter((account) => profiles.includes(account.value));
          setAccounts(filteredAccounts);
        } else setAccounts(finalResult);
      } catch (error) {
        console.error("Error loading header data:", error);
        // Set default user name if everything fails
        setUser("User");
        setAccounts([]);
      }
    })();
  }, []);

  useEffect(() => {
    const selectedValue = localStorage.getItem("selectedAccountValue");
    if (!selectedValue || accounts.length === 0) return;

    const matched = accounts.find((acc) => acc.value === selectedValue);
    if (matched?.country) {
      setSelectedAccountCountry(matched.country);
      localStorage.setItem("selectedAccountCountry", matched.country);
    }
    if (matched?.entity !== undefined) {
      setSelectedAccountEntity(matched.entity || "");
      localStorage.setItem("selectedAccountEntity", matched.entity || "");
    }
    if (matched?.label) {
      setSelectedAccountLabel(matched.label);
      localStorage.setItem("selectedAccountLabel", matched.label);
    }
  }, [accounts]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        !Object.values(dropdownRefs).some(
          (ref) => ref.current && ref.current.contains(event.target),
        )
      )
        setActiveDropdown(null);
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [dropdownRefs]);

  // Socket.IO: Listen for real-time report progress events
  useEffect(() => {
    console.log("🔔 Header: Socket listeners attached");

    const handleProgress = (data) => {
      console.log("🔔 Header: report:progress received", data);
      setActiveReportTask({
        percent: data.percent,
        stage: data.stage,
        message: data.message,
        accountId: data.accountId,
        date: data.date,
      });
    };

    const handleComplete = (data) => {
      console.log("🔔 Header: report:complete received", data);
      setActiveReportTask(null);
      setReportNotifications((prev) => [
        {
          id: Date.now(),
          type: data.success === false ? "error" : "success",
          message: data.message || "Report fetch completed!",
          date: data.date,
          timestamp: new Date().toLocaleTimeString(),
        },
        ...prev.slice(0, 4),
      ]);
    };

    const handleError = (data) => {
      console.log("🔔 Header: report:error received", data);
      setActiveReportTask(null);
      setReportNotifications((prev) => [
        {
          id: Date.now(),
          type: "error",
          message: data.message || "Report fetch failed.",
          date: data.date,
          timestamp: new Date().toLocaleTimeString(),
        },
        ...prev.slice(0, 4),
      ]);
    };

    socket.on("report:progress", handleProgress);
    socket.on("report:complete", handleComplete);
    socket.on("report:error", handleError);

    return () => {
      socket.off("report:progress", handleProgress);
      socket.off("report:complete", handleComplete);
      socket.off("report:error", handleError);
    };
  }, []);

  const handleAccountChange = (selected) => {
    if (selected) {
      alertify
        .confirm(
          "Confirm Account Switch",
          "Are you sure you want to switch to account " +
            selected.label +
            "",
          () => {
            localStorage.setItem("selectedAccountLabel", selected.label);
            localStorage.setItem("selectedAccountValue", selected.value);
            localStorage.setItem("selectedAccountName", selected.label);
            localStorage.setItem("selectedAccountCountry", selected.country || "US");
            localStorage.setItem("selectedAccountEntity", selected.entity || "");
            setSelectedAccountLabel(selected.label);
            setSelectedAccountCountry(selected.country || "US");
            setSelectedAccountEntity(selected.entity || "");
            setActiveDropdown(null);

            // Trigger custom event to notify change
            window.dispatchEvent(new Event("accountChanged"));

            alertify.success("Account switched to " + selected.label);
            alertify.warning("Refreshing page...");

            setTimeout(() => {
              window.location.reload();
            }, 2000);
          },
          () => {
            setActiveDropdown(null);
            alertify.error("Account switch canceled");
          },
        ).set("labels", { ok: "Yes, switch", cancel: "Cancel" });
    } else {
      setActiveDropdown(null);
    }
  };

  const handleViewAllSugerencias = () => {
    navigate("/sugerencias");
    setActiveDropdown(null);
  };

  const handleLlmModelChange = (selected) => {
    if (!selected) {
      setActiveDropdown(null);
      return;
    }

    localStorage.setItem("selectedLlmModel", selected.value);
    localStorage.setItem("selectedLlmModelLabel", selected.label);
    setSelectedLlmModel(selected.label);
    setActiveDropdown(null);
    alertify.success(`AI model changed to ${selected.label}`);
  };

  const handleCancelProcess = (e) => {
    e.stopPropagation(); // Evitar cerrar el dropdown
    alertify.confirm(
      "Stop Process",
      "Are you sure you want to cancel the current download process?",
      () => {
        if (activeReportTask) {
          socket.emit("report:cancel", { accountId: activeReportTask.accountId });
        }
        setActiveReportTask(null);
        alertify.error("Process stopped.");
      },
      () => {}
    ).set("labels", { ok: "Yes, stop", cancel: "No, continue" });
  };

  return (
    <nav className="navbar navbar-expand-lg bg-body-tertiary fixed-top">
      <div className="container-fluid">
        <Sidebar />
        <a className="navbar-brand d-flex align-items-center" href="/dashboard">
          <img src={Logo} alt="Logo" width={40} className="me-2" /> Jungle Click
        </a>
        <button
          className="navbar-toggler"
          type="button"
          data-bs-toggle="collapse"
          data-bs-target="#navbarNav"
          aria-controls="navbarNav"
          aria-expanded="false"
          aria-label="Toggle navigation"
        >
          <span className="navbar-toggler-icon"></span>
        </button>
        <div className="collapse navbar-collapse" id="navbarNav">
          <ul className="navbar-nav ms-auto">
            <li className="nav-item">
              <button
                className="btn btn-link nav-link"
                onClick={() => navigate("/dashboard")}
              >
                Home
              </button>
            </li>

            <li
              className="nav-item dropdown justify-content-start mx-2"
              ref={dropdownRefs.dropdown3}
            >
              <button
                className="btn btn-link nav-link dropdown-toggle w-auto"
                onClick={() => toggleDropdown("dropdown3")}
                aria-expanded={activeDropdown === "dropdown3"}
              >
                <FontAwesomeIcon icon={faGlobe} className="me-2" />{" "}
                {selectedAccountLabel}
                <span className="badge bg-light text-dark border ms-2">
                  {selectedAccountCountry || "US"}
                </span>
                {selectedAccountEntity && (
                  <span className="badge bg-success text-light border ms-2">
                    {selectedAccountEntity}
                  </span>
                )}
              </button>
              {activeDropdown === "dropdown3" && (
                <div
                  className="dropdown-menu show p-2"
                  style={{
                    minWidth: "250px",
                    maxWidth: "350px",
                  }}
                >
                  <div className="mb-2 border-bottom pb-2">
                    <span className="fw-bold">Current Account:</span>{" "}
                    {selectedAccountLabel}
                    <span className="badge bg-light text-dark border ms-2">
                      {selectedAccountCountry || "US"}
                    </span>
                    {selectedAccountEntity && (
                      <span className="badge bg-success text-light border ms-2">
                        {selectedAccountEntity}
                      </span>
                    )}
                  </div>
                  <Select
                    options={accounts}
                    placeholder="Search account..."
                    isClearable
                    isSearchable={true}
                    onChange={handleAccountChange}
                    value={null}
                    formatOptionLabel={formatOptionLabel}
                    styles={{
                      container: (base) => ({ ...base, width: "100%" }),
                      menu: (base) => ({
                        ...base,
                        zIndex: 9999,
                        position: "relative",
                      }),
                      menuList: (base) => ({
                        ...base,
                        maxHeight: "200px",
                        overflowY: "auto",
                      }),
                      option: (base, state) => ({
                        ...base,
                        padding: "8px 12px",
                      }),
                    }}
                  />
                </div>
              )}
            </li>

            <li
              className="nav-item dropdown justify-content-start mx-2"
              ref={dropdownRefs.dropdown2}
            >
              <button
                className="btn btn-link nav-link dropdown-toggle w-auto"
                style={{ fontSize: '0.9rem', fontWeight: '500' }}
                onClick={() => toggleDropdown("dropdown2")}
                aria-expanded={activeDropdown === "dropdown2"}
              >
                {selectedLlmModel}
              </button>
              {activeDropdown === "dropdown2" && (
                <div
                  className="dropdown-menu show p-2"
                  style={{
                    minWidth: "220px",
                    maxWidth: "320px",
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                    borderRadius: '8px'
                  }}
                >
                  <div className="mb-2 border-bottom pb-2 px-1">
                    <span className="fw-bold text-muted small">Choose AI Model</span>
                  </div>
                  <Select
                    options={llmModels}
                    placeholder="Select AI model..."
                    isClearable={false}
                    isSearchable={false}
                    onChange={handleLlmModelChange}
                    value={llmModels.find((m) => m.label === selectedLlmModel) || null}
                    styles={{
                      container: (base) => ({ ...base, width: "100%" }),
                      control: (base) => ({ 
                        ...base, 
                        borderRadius: '6px',
                        borderColor: '#dee2e6'
                      }),
                      menu: (base) => ({
                        ...base,
                        zIndex: 9999,
                        position: "relative",
                      }),
                    }}
                  />
                </div>
              )}
            </li>

            <li
              className="nav-item dropdown justify-content-start mx-2"
              ref={dropdownRefs.dropdown1}
            >
              <button
                className="btn btn-link nav-link position-relative"
                onClick={() => toggleDropdown("dropdown1")}
                aria-expanded={activeDropdown === "dropdown1"}
                style={{ fontSize: "1.1rem" }}
              >
                <FontAwesomeIcon icon={faBell} />
                {/* Spinning badge while a report is fetching */}
                {activeReportTask && (
                  <span
                    className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-warning"
                    style={{ fontSize: "0.55rem", padding: "3px 5px" }}
                  >
                    <FontAwesomeIcon icon={faSpinner} spin />
                  </span>
                )}
                {/* Dot badge for pending notifications */}
                {!activeReportTask && (sugerencias.length > 0 || reportNotifications.length > 0) && (
                  <span className="badge bg-primary ms-1">
                    {sugerencias.length + reportNotifications.length}
                  </span>
                )}
              </button>
              {activeDropdown === "dropdown1" && (
                <ul
                  className="dropdown-menu show"
                  style={{ minWidth: "320px", maxWidth: "380px", right: 0, left: "auto" }}
                >
                  {/* ── Active report fetch progress ── */}
                  {activeReportTask && (
                    <li className="px-3 pt-2 pb-1">
                      <div className="d-flex align-items-start justify-content-between mb-1 w-100">
                        <div className="d-flex align-items-start">
                          <FontAwesomeIcon icon={faSpinner} spin className="text-warning me-2 mt-1" />
                          <strong style={{ fontSize: "0.82rem", lineHeight: "1.2" }}>Fetching report data…</strong>
                        </div>
                        <button 
                          className="btn btn-link btn-sm p-0 text-danger ms-auto" 
                          style={{ textDecoration: 'none', fontWeight: 'bold' }}
                          onClick={handleCancelProcess}
                          title="Stop Process"
                        >
                          ✕
                        </button>
                      </div>
                      <div className="text-muted" style={{ fontSize: "0.75rem", marginBottom: "4px" }}>
                        {activeReportTask.message}
                      </div>
                      <div className="progress" style={{ height: "8px", borderRadius: "4px" }}>
                        <div
                          className="progress-bar progress-bar-striped progress-bar-animated bg-warning"
                          role="progressbar"
                          style={{ width: `${activeReportTask.percent}%` }}
                          aria-valuenow={activeReportTask.percent}
                          aria-valuemin="0"
                          aria-valuemax="100"
                        />
                      </div>
                      <div className="text-end text-muted mt-1" style={{ fontSize: "0.72rem" }}>
                        {activeReportTask.percent}% — {activeReportTask.date}
                      </div>
                      <hr className="my-2" />
                    </li>
                  )}

                  {/* ── Completed / Error report notifications ── */}
                  {reportNotifications.map((notif) => (
                    <li key={notif.id} className="px-3 py-1">
                      <div className="d-flex align-items-start justify-content-between w-100">
                        <div className="d-flex align-items-start">
                          <FontAwesomeIcon
                            icon={notif.type === "success" ? faCheckCircle : faTimesCircle}
                            className={`me-2 mt-1 ${notif.type === "success" ? "text-success" : "text-danger"}`}
                            style={{ fontSize: "0.85rem" }}
                          />
                          <div>
                            <div style={{ fontSize: "0.78rem", fontWeight: 500 }}>{notif.message}</div>
                            <div className="text-muted" style={{ fontSize: "0.7rem" }}>
                              {notif.date} — {notif.timestamp}
                            </div>
                          </div>
                        </div>
                        <button
                          className="btn btn-link btn-sm p-0 ms-auto text-muted"
                          style={{ fontSize: "0.7rem", lineHeight: 1 }}
                          onClick={() =>
                            setReportNotifications((prev) => prev.filter((n) => n.id !== notif.id))
                          }
                        >
                          ✕
                        </button>
                      </div>
                      <hr className="my-2" />
                    </li>
                  ))}

                  {/* ── Suggestions ── */}
                  {sugerencias.length > 0 ? (
                    <>
                      {sugerencias.slice(0, 5).map((sug, index) => (
                        <li key={index}>
                          <span className="dropdown-item w-auto">
                            User recommendation {sug.usuario}
                          </span>
                        </li>
                      ))}
                      <li>
                        <button
                          className="dropdown-item text-center fw-bold"
                          onClick={handleViewAllSugerencias}
                        >
                          View all
                        </button>
                      </li>
                    </>
                  ) : (
                    !activeReportTask && reportNotifications.length === 0 && (
                      <li>
                        <span className="dropdown-item w-auto text-muted" style={{ fontSize: "0.85rem" }}>
                          No notifications.
                        </span>
                      </li>
                    )
                  )}
                </ul>
              )}
            </li>


            <li className="nav-item mx-2">
              <span className="nav-link text-start">{user}</span>
            </li>
          </ul>
        </div>
      </div>
    </nav>
  );
}

export default Header;
